import { VICTIMS as VICTIM_DATA, MATCHUPS } from '../../constants/victim'
import { MATCHUP_ROUND_CHANCE } from '../../constants/game'
import { Victim, VictimTag } from '../../types/victimTypes'
import { StatsService } from '../StatsService/StatsService'

const VICTIMS: Victim[] = VICTIM_DATA as Victim[]

export type RunKill = {
  victim: Victim
  /** Crowd kill rate (0–100) at the moment of the choice, before this kill. */
  baselinePercent: number
}

/** Everything that happened this run, for the HUD, type lines and verdict. */
export type RunTally = {
  kills: RunKill[]
  spared: Victim[]
  lovedOneStreak: number
}

const emptyTally = (): RunTally => ({
  kills: [],
  spared: [],
  lovedOneStreak: 0,
})

export const percentKilled = (victim: Victim): number => {
  const appearances = Math.max(victim.appearances, 1)
  return Math.min(Math.round((victim.deathSentences / appearances) * 100), 100)
}

/**
 * Owns victim selection and the running kill stats. Victims come from the
 * constants; per-victim stats are overlaid from StatsService (localStorage).
 */
export class VictimService {
  public victimOne: Victim
  public victimTwo: Victim
  public doomedVictim: Victim | undefined

  private victims: Victim[]
  private encounteredNames = new Set<string>()
  private statsService: StatsService
  private run: RunTally = emptyTally()

  constructor(statsService: StatsService = new StatsService()) {
    this.statsService = statsService
    this.victims = VICTIMS.map((victim) => ({
      ...victim,
      ...this.statsService.getStats(victim.name, {
        appearances: victim.appearances,
        deathSentences: victim.deathSentences,
      }),
    }))

    // Round one is always a curated matchup: the strongest first impression
    // the roster has, and every one of them is a two-hander joke.
    const opener = this.pickMatchup([], true)
    this.victimOne = this.pickNewVictim([], opener?.[0])
    this.victimTwo = this.pickNewVictim([], opener?.[1])
    // Warm the first pair now: the intro plays for ~10 s and the art is
    // ~1.5 MB apiece, so round one must not open on two black halves.
    this.preloadImages()
  }

  /**
   * Picks a random victim respecting the lap (no repeats until everyone
   * has been seen) and whatever is on screen. When `preferredName` is
   * given and is a legal candidate it wins; otherwise selection is
   * uniform — so curated matchups can never bypass the lap rules.
   */
  private pickNewVictim(
    alsoExclude: string[] = [],
    preferredName?: string,
  ): Victim {
    const inUse = new Set(
      [
        this.victimOne?.name,
        this.victimTwo?.name,
        ...alsoExclude,
      ].filter(Boolean),
    )
    let candidates = this.victims.filter(
      (victim) =>
        !inUse.has(victim.name) && !this.encounteredNames.has(victim.name),
    )

    // Every victim has been seen this session — start a fresh lap. inUse
    // still applies, so the pair on screen can't come straight back.
    if (candidates.length === 0) {
      this.encounteredNames.clear()
      candidates = this.victims.filter((victim) => !inUse.has(victim.name))
    }

    const preferred = preferredName
      ? candidates.find((victim) => victim.name === preferredName)
      : undefined
    const victim =
      preferred ?? candidates[Math.floor(Math.random() * candidates.length)]
    this.encounteredNames.add(victim.name)
    return victim
  }

  /**
   * A curated pair whose halves are both unseen this lap and not on
   * screen, in random left/right order — or undefined (most rounds).
   */
  private pickMatchup(
    exclude: string[],
    force = false,
  ): [string, string] | undefined {
    if (!force && Math.random() >= MATCHUP_ROUND_CHANCE) return undefined
    const blocked = new Set([
      ...exclude,
      this.victimOne?.name,
      this.victimTwo?.name,
      ...this.encounteredNames,
    ])
    const eligible = MATCHUPS.filter(
      ([a, b]) => !blocked.has(a) && !blocked.has(b),
    )
    if (eligible.length === 0) return undefined
    const [a, b] = eligible[Math.floor(Math.random() * eligible.length)]
    return Math.random() < 0.5 ? [a, b] : [b, a]
  }

  /**
   * Called when a victim pair is actually shown to the player, so idle
   * page loads don't inflate appearance counts (and deflate kill rates).
   */
  public recordCurrentPairAppearance(): void {
    for (const victim of [this.victimOne, this.victimTwo]) {
      victim.appearances++
      this.statsService.recordAppearance(victim.name)
    }
  }

  public replaceVictims(): void {
    // Exclude the outgoing pair explicitly: if the lap resets mid-replace,
    // the just-shown (possibly just-killed) victim must not reappear.
    const outgoing = [this.victimOne.name, this.victimTwo.name]
    const matchup = this.pickMatchup(outgoing)
    this.victimOne = this.pickNewVictim(outgoing, matchup?.[0])
    this.victimTwo = this.pickNewVictim(outgoing, matchup?.[1])
    this.preloadImages()
  }

  private preloadImages(): void {
    for (const victim of [this.victimOne, this.victimTwo]) {
      const img = new Image()
      img.src = victim.image
    }
  }

  public doomVictim(victimName: string): void {
    const doomed =
      victimName === this.victimOne.name ? this.victimOne : this.victimTwo
    const spared = doomed === this.victimOne ? this.victimTwo : this.victimOne

    // Tally before the increment so the baseline is the crowd's verdict,
    // not the crowd's plus this player's.
    this.run.kills.push({ victim: doomed, baselinePercent: percentKilled(doomed) })
    this.run.spared.push(spared)
    this.run.lovedOneStreak = doomed.tags.includes('loved-one')
      ? this.run.lovedOneStreak + 1
      : 0

    this.doomedVictim = doomed
    doomed.deathSentences++
    this.statsService.recordDeathSentence(doomed.name)
  }

  public getPercentKilled(): string {
    return String(this.doomedVictim ? percentKilled(this.doomedVictim) : 0)
  }

  public getNumTimesKilled(): string {
    return String(this.doomedVictim?.deathSentences ?? 0)
  }

  public getRunTally(): RunTally {
    return this.run
  }

  public getBodyCount(): number {
    return this.run.kills.length
  }

  public resetRun(): void {
    this.run = emptyTally()
  }

  /** Kill/spare counts per tag for "you have a type" lines. */
  public getTagTally(): { killed: Record<VictimTag, number>; spared: Record<VictimTag, number> } {
    const zero = (): Record<VictimTag, number> => ({
      animal: 0,
      human: 0,
      food: 0,
      'loved-one': 0,
      object: 0,
      pop: 0,
    })
    const killed = zero()
    const spared = zero()
    for (const { victim } of this.run.kills) {
      for (const tag of victim.tags) killed[tag]++
    }
    for (const victim of this.run.spared) {
      for (const tag of victim.tags) spared[tag]++
    }
    return { killed, spared }
  }
}
